<template>
    <div v-show="show" class="loading-overlay" :class="{ 'with-navigate-button': showNavigateButton }">
        <div class="loading-content" :class="{ 'with-navigate-button': showNavigateButton }">
            <div class="loading-spinner">
                <img src="/img/images/loading_edna.gif" class="loading-logo"></img>
            </div>
            <h3 class="loading-title">{{ currentPhase === 'prompts' ? 'Drafting your play...' : `Creating ${totalAssets} ${totalAssets === 1 ? 'Touchpoint' : 'Touchpoints'}` }} <em v-if="totalAssets > 0 && currentPhase !== 'prompts'">({{percentage(assetsGenerated / totalAssets)}} finished)</em></h3>
            <div class="loading-subtitle" v-if="fundraisingFacts.length > 0">
                <transition name="fade" mode="out-in">
                    <p :key="currentFactIndex">
                        <span class="loading-fact-title d-block text-center">{{ currentFactHeadline }}</span> 
                        <span class="loading-fact-text d-block text-center">{{ fundraisingFacts[currentFactIndex].fact }}</span>

                        <a v-if="fundraisingFacts[currentFactIndex].url"
                            :href="fundraisingFacts[currentFactIndex].url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="loading-fact-source d-block text-center">
                            Source: {{ fundraisingFacts[currentFactIndex].source }}
                        </a>
                        <span v-else class="loading-fact-source d-block text-center">
                            Source: {{ fundraisingFacts[currentFactIndex].source }}
                        </span>
                    </p>
                </transition>
            </div>
            
            <div v-if="currentPhase === 'assets' || showNavigateButton" class="loading-footer">
                <div v-if="currentPhase === 'assets' && phase != 'assets'">
                    <p class="loading-footer-title">Your strategy is complete.</p>
                    <p class="loading-footer-subtitle">Touchpoints are now generating. You can review your strategy while you wait.</p>
                    <button class="btn btn-secondary" @click="handleNavigate">
                        Review Strategy <img src="/img/icons/avid-icons/chevron-right.svg" class="icon" alt=""></img>
                    </button>
                </div>
                <div v-else-if="currentPhase === 'assets'">
                    <p class="loading-footer-title">Your inputs are complete.</p>
                    <p class="loading-footer-subtitle">We're drafting your experiment treatment which may take a few minutes.</p>
                </div>
                <div v-else-if="showNavigateButton">
                    <p class="loading-footer-title">Good things take time.</p>
                    <p class="loading-footer-subtitle">You can keep waiting or you can proceed to the play builder to continue your work.
                    </p>
                    <button class="btn btn-secondary" @click="handleNavigate">
                        Proceed to Play Builder <img src="/img/icons/avid-icons/chevron-right.svg" class="icon" alt=""></img>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useClientStore } from '../../stores/client';
import Swal from 'sweetalert2';

export default {
    name: 'PlaybookLoadingScreen',

    emits: ['navigate'],
    props: {
        show: {
            type: Boolean,
            default: false
        },
        playbookId: {
            type: [Number, null],
            required: false,
            default: null
        },
        playbookUrl: {
            type: [String, null],
            required: false,
            default: null
        },
        totalAssets: {
            type: Number,
            default: 0
        },
        phase: {
            type: String,
            default: 'prompts'
        }
    },
    setup() {
        const clientStore = useClientStore();
        return {
            clientStore
        };
    },
    data() {
        return {
            currentPhase: this.phase, // 'prompts' or 'assets'
            assetsGenerated: 0,
            fundraisingFacts: [],
            currentFactIndex: 0,
            factRotationInterval: null,
            echoChannel: null,
            startTime: null,
            navigateButtonTimer: null,
            navigateReady: false,
        }
    },
    computed: {
        client() {
            return this.clientStore.getClient;
        },
        showNavigateButton() {
            return this.currentPhase !== 'assets' && this.navigateReady;
        },
        currentFactHeadline() {
            if (this.fundraisingFacts.length === 0) {
                return 'Did you know?';
            }
            
            const currentFact = this.fundraisingFacts[this.currentFactIndex];
            if (currentFact && currentFact.type === 'encouragement') {
                return 'You\'re Amazing!';
            }
            return 'Did you know?';
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.startTimer();
                this.startFactRotation();
                this.subscribeToEvents();
            } else {
                this.cleanup();
            }
        }
    },
    mounted() {
        this.fetchQuotes();
        if (this.show) {
            this.startTimer();
            this.startFactRotation();
            this.subscribeToEvents();
        }
    },
    beforeUnmount() {
        this.cleanup();
    },
    methods: {
        fetchQuotes() {
            if (!this.client || !this.client.url) {
                return;
            }
            
            window.axios.get(`/api/playbooks/${this.client.url}/quotes`)
                .then(response => {
                    if (response.data.data && response.data.data.length > 0) {
                        // Transform API response to match the component's expected format
                        this.fundraisingFacts = response.data.data.map(quote => ({
                            fact: quote.quote,
                            source: quote.source,
                            url: quote.url,
                            type: quote.type
                        }));
                    }
                    // If no quotes available, array stays empty - UI will handle gracefully
                })
                .catch(error => {
                    console.error('Failed to fetch playbook quotes:', error);
                    // On error, array stays empty - UI will handle gracefully
                });
        },
        startTimer() {
            this.startTime = Date.now();
            this.navigateReady = false;
            this.navigateButtonTimer = setTimeout(() => {
                this.navigateReady = true;   // <— reactive change triggers update
            }, 60000); // 60 seconds = 60000 milliseconds
        },
        startFactRotation() {
            // Only start rotation if we have facts
            if (this.fundraisingFacts.length === 0) {
                return;
            }
            
            // Rotate facts every 8 seconds - advance to next index (maintains interleaved order)
            this.factRotationInterval = setInterval(() => {
                if (this.fundraisingFacts.length === 0) {
                    return;
                }
                
                // Move to next index, wrap around at the end
                this.currentFactIndex = (this.currentFactIndex + 1) % this.fundraisingFacts.length;
            }, 10000);
        },
        subscribeToEvents() {
            if (window.Echo && this.client && this.client.id) {
                this.echoChannel = window.Echo.private('client.' + this.client.id)
                    .listen('PlaybookPromptsGenerated', (e) => {
                        if (this.playbookId && e.playbook_id === this.playbookId) {
                            // Check if this is an error response by fetching content
                            window.axios.get(`/api/playbooks/${this.client.url}/content/${e.content_key}`)
                                .then(response => {
                                    if (response.data.content && response.data.content.error) {
                                        // Handle error case
                                        this.handlePromptsError(response.data.content.error);
                                        return;
                                    }
                                    
                                    // Success case - proceed to assets phase
                                    this.currentPhase = 'assets';

                                    if(this.totalAssets === 0) {
                                        this.cleanup();
                                        this.$emit('navigate', this.playbookUrl);
                                    }
                                })
                                .catch(error => {
                                    console.error('Failed to fetch prompts content:', error);
                                    // On API error, still proceed but show a generic error
                                    this.handlePromptsError('Something went wrong when generating your Strategy Brief. You will be redirected to the play builder to try again.');
                                });
                        }
                    })
                    .listen('AssetUpdated', (e) => {
                        if (this.playbookId && e.playbook_id === this.playbookId) {
                            if (e.source === 'generated') {
                                this.assetsGenerated++;
                                
                                // Check if all assets have been generated
                                if (this.assetsGenerated >= this.totalAssets) {
                                    this.cleanup();
                                    this.$emit('navigate', this.playbookUrl);
                                }
                            }
                        }
                    });
            }
        },
        handlePromptsError(errorMessage) {
            // Show error popup and navigate when user clicks OK
            this.cleanup();
            
            Swal.fire({
                html: '<h4 class="mb-0">' + errorMessage + '</h4>',
                icon: 'error',
                showCancelButton: false,
                confirmButtonText: 'OK',
                customClass: {
                    popup: 'swal-error'
                },
            }).then(() => {
                // Navigate after user clicks OK
                this.$emit('navigate', this.playbookUrl);
            });
        },
        handleNavigate() {
            this.$emit('navigate', this.playbookUrl);
        },
        cleanup() {
            if (this.factRotationInterval) {
                clearInterval(this.factRotationInterval);
                this.factRotationInterval = null;
            }
            if (this.navigateButtonTimer) {
                clearTimeout(this.navigateButtonTimer);
                this.navigateButtonTimer = null;
            }
            this.navigateReady = false; 
            if (window.Echo && this.client && this.client.id) {
                window.Echo.private('client.' + this.client.id)
                    .stopListening('PlaybookPromptsGenerated')
                    .stopListening('AssetUpdated');
            }
            this.startTime = null;
        }
    }
}
</script>

